import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { TagType } from "src/enums/tagEnums";
import {
  BreadcrumbsViewModel,
  MediaCategoryTypeNameEnum,
  MediaTypeNameEnum,
  MediaViewModel,
  PageViewModel,
  ProductViewModel,
  WarehouseStockStatusEnum,
} from "src/domain/client";
import { environment } from "src/environments/environment";
import { MediaPipe } from "src/pipes/media.pipe";
import { TagPipe } from "src/pipes/tag.pipe";
import { ActivityLogService } from "src/services/activity-log.service";
import { BrandService } from "src/services/brand.service";
import { BreadcrumbsService } from "src/services/breadcrumbs.service";
import { ChannelRequestService } from "src/services/channel-request.service";
import { ErrorReporterService } from "src/services/error-reporter.service";
import { ModelAndDealerService } from "src/services/model-and-dealer.service";
import { PageService } from "src/services/page.service";
import { PassingControlService } from "src/services/passing-control.service";
import { ProductService } from "src/services/product.service";
import { ShoppingCartService } from "src/services/shopping-cart.service";
import { ToastService } from "src/services/toast.service";

interface RouteParams {
  sku?: string;
}

@Component({
  selector: "vw-product-page",
  templateUrl: "./product-page.component.html",
})
export class ProductPageComponent implements OnInit, OnDestroy {
  product?: ProductViewModel;
  sku?: string;
  productError = false;
  page?: PageViewModel;
  mediaCategoryTypeNames = MediaCategoryTypeNameEnum;
  mediaTypes = MediaTypeNameEnum;
  images: MediaViewModel[] = [];
  productImageFallbackURL = environment.productImageFallbackURL;
  breadcrumbs: BreadcrumbsViewModel[] = [];
  productMessages: string[] = [];
  linkEULabelingTyres?: string;
  productName?: string;
  productDescription?: string;
  technicalInformation?: string;
  productLoading = false;
  warehouseStockStatusEnum = WarehouseStockStatusEnum;

  form = new FormGroup({
    quantity: new FormControl(1, [Validators.required, Validators.min(1)]),
  });

  private paramSubscription?: Subscription;
  private channelRequestSubscription?: Subscription;

  constructor(
    public channelRequestService: ChannelRequestService,
    public shoppingCartService: ShoppingCartService,
    private modelAndDealerService: ModelAndDealerService,
    private route: ActivatedRoute,
    private productService: ProductService,
    private pageService: PageService,
    private brandService: BrandService,
    private toastService: ToastService,
    private errorReporter: ErrorReporterService,
    private translateService: TranslateService,
    private activityLogService: ActivityLogService,
    private breadcrumbsService: BreadcrumbsService,
    private passingControlService: PassingControlService,
    private mediaPipe: MediaPipe,
    private tagPipe: TagPipe,
  ) {}

  ngOnInit(): void {
    this.getPage();

    this.paramSubscription = this.route.params.subscribe(
      (params: RouteParams) => {
        this.sku = params.sku;

        this.getProduct();
      },
    );

    this.channelRequestSubscription =
      this.channelRequestService.channelRequestUpdated$.subscribe(() => {
        this.getProduct(false);
      });
  }

  ngOnDestroy(): void {
    this.paramSubscription?.unsubscribe();
    this.channelRequestSubscription?.unsubscribe();
  }

  openSelectDealerModal(): void {
    this.modelAndDealerService.openSelectDealer();
  }

  addToCart(): void {
    if (
      !this.form.valid ||
      this.product?.productId == null ||
      this.product.sku == null
    ) {
      return;
    }

    this.shoppingCartService.addShoppingCartItem(
      this.product.productId,
      this.product.sku,
      this.product.minimumQuantity,
      this.product.media,
      this.form.value.quantity ?? 0,
    );
  }

  //////////

  private getProduct(log = true): void {
    this.product = undefined;
    this.productLoading = true;
    this.productError = false;
    this.images = [];
    if (this.sku != null) {
      this.productService
        .getProduct(this.sku)
        .then((product) => {
          this.product = product;
          this.getProductSuccessCallback(product, log);
        })
        .catch(() => {
          this.productError = true;
        })
        .finally(() => {
          this.productLoading = false;
        });
    }
  }

  private getProductSuccessCallback(
    product: ProductViewModel,
    log = false,
  ): void {
    if (log && product.sku != null) {
      this.logProductVisit(product.sku);
    }

    if (product.productId != null) this.getBreadcrumbs(product.productId);
    this.filterImages(product);
    this.passingControl(product);
    this.mapProductMessages(product);
    this.mapProductTagsAndMedia(product);
  }

  private getPage(): void {
    this.pageService
      .getProductPage(this.brandService.brandId)
      .then((page) => {
        this.page = page;
      })
      .catch(this.errorReporter.handle);
  }

  private getBreadcrumbs(productId: number): void {
    this.breadcrumbsService
      .getCategoryHierarchyByProductId(productId)
      .then((breadcrumbs) => {
        this.breadcrumbs = breadcrumbs;
      })
      .catch(() => {});
  }

  private logProductVisit(sku: string): void {
    if (this.channelRequestService.channelRequest?.channelRequestId) {
      this.activityLogService
        .logProductVisit(
          this.channelRequestService.channelRequest.channelRequestId,
          sku,
        )
        .catch(() => {});
    }
  }

  private filterImages(product: ProductViewModel): void {
    this.images =
      product.media?.filter(
        (_) =>
          _.mediaTypeName === MediaTypeNameEnum.Image &&
          (_.mediaCategoryTypeName ===
            MediaCategoryTypeNameEnum.ImageNameSmall ||
            _.mediaCategoryTypeName ===
              MediaCategoryTypeNameEnum.ImageNameMedium ||
            _.mediaCategoryTypeName ===
              MediaCategoryTypeNameEnum.ImageNameLarge),
      ) ?? [];
  }

  private passingControl(product: ProductViewModel): void {
    if (product.productId == null) {
      this.errorReporter.handle(new Error("productId is null"));
      return;
    }
    if (this.channelRequestService.channelRequest?.channelRequestId == null) {
      this.errorReporter.handle(new Error("channelRequestId is null"));
      return;
    }
    this.passingControlService
      .passingControlByProductId(
        product.productId,
        this.channelRequestService.channelRequest.channelRequestId,
      )
      .then((passes) => {
        if (!passes) {
          this.showProductDoesNotPassMessage(product);
        }
      })
      .catch(this.errorReporter.handle);
  }

  private showProductDoesNotPassMessage(product: ProductViewModel): void {
    this.toastService.createTopBannerMessage(
      this.translateService.instant(
        "topBannerMessages.productDoesNotPass.title",
        {
          productName: this.mediaPipe.transform(
            product.media,
            MediaCategoryTypeNameEnum.ProductName,
            MediaTypeNameEnum.Text,
          ),
        },
      ),
      this.translateService.instant(
        "topBannerMessages.productDoesNotPass.text",
      ),
      false,
    );
  }

  private mapProductMessages(product: ProductViewModel): void {
    this.productMessages = [];

    if (!!product.productSubset?.length) {
      const assembledAndReadyTag = this.tagPipe.transform(
        product.productTags,
        TagType.AssembledAndReady,
      );

      if (!!assembledAndReadyTag && !!Number(assembledAndReadyTag.tag.value)) {
        this.productMessages.push(
          this.translateService.instant("assembledAndReady"),
        );
      } else {
        this.productMessages.push(
          this.translateService.instant("productSubset"),
        );
      }
    }

    const productNewTag = this.tagPipe.transform(
      product.productTags,
      TagType.ProductNew,
    );

    if (
      !!productNewTag &&
      !!productNewTag.tag &&
      !!Number(productNewTag.tag.value)
    ) {
      this.productMessages.push(this.translateService.instant("news"));
    }
    // Campaign category is going to be temporarily invisible for external
    // const productCampaignTag = this.tagPipe.transform(
    //   product.productTags,
    //   TagTypeEnum.Campaign
    // );

    // const productCampaignTag = this.tagPipe.transform(
    //   product.productTags,
    //   TagTypeEnum.Campaign
    // );

    // if (
    //   !!productCampaignTag &&
    //   !!productCampaignTag.tag &&
    //   !!+productCampaignTag.tag.value
    // ) {
    //   this.productMessages.push(this.translateService.instant("campaign"));
    // }
  }

  private mapProductTagsAndMedia(product: ProductViewModel): void {
    this.linkEULabelingTyres = this.tagPipe.transform(
      product.productTags,
      TagType.LinkEUlabelingTires,
    )?.tag.value;

    this.productName = this.mediaPipe.transform(
      product.media,
      MediaCategoryTypeNameEnum.ProductName,
      MediaTypeNameEnum.Text,
    );

    this.productDescription = this.mediaPipe.transform(
      product.media,
      MediaCategoryTypeNameEnum.Description,
      MediaTypeNameEnum.Text,
    );

    this.technicalInformation = this.mediaPipe.transform(
      product.media,
      MediaCategoryTypeNameEnum.Technical,
      MediaTypeNameEnum.HTML,
    );
  }
}
